import { useResizeObserver } from "@mantine/hooks";
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { evolveState } from "./ZFEvolve";
import { SessionResults } from "@/components/practitioner/champVisuel/ChampVisuelParameter";

type MaillageCell = {
  successes: number;
  total: number;
};

type MaillageType = MaillageCell[][];
type SelectedCellsType = number[][];

interface LegendProps {
  currentZone: number;
  setCurrentZone: (zone: number) => void;
  deselectAllCells: () => void;
  selectedCells: SelectedCellsType;
}

interface ResultType {
  position: string;
  reactionTime?: string | number;
  successCount: string;
  failedCount?: number;
}

interface ResultType2 {
  position: string;
  successCount: number;
  failedCount: number;
}

const Legend = ({
  currentZone,
  setCurrentZone,
  deselectAllCells,
  selectedCells,
}: LegendProps) => {
  const legendItems = [
    { color: "bg-yellow-500", label: "Zone Frontière", zone: 1 },
    { color: "bg-red-500", label: "Zone Profonde", zone: 2 },
    { color: "bg-green-500", label: "Zone Saine", zone: 3 },
    { color: "bg-white", label: "Zone Neutre", zone: 0 },
  ];

  // Calculer le nombre de cases sélectionnées pour chaque zone
  const countCellsByZone = (zone: number) => {
    return selectedCells.flat().filter(cell => cell === zone).length;
  };

  return (
    <div className="flex justify-center mt-4 bg-black p-4 rounded-lg">
      {legendItems.map((item) => {
        const count = countCellsByZone(item.zone);
        return (
          <div
            key={item.zone}
            onClick={() => setCurrentZone(item.zone)}
            className={`flex flex-col items-center cursor-pointer mr-4 p-3 rounded ${
              currentZone === item.zone ? "border-2 border-white" : ""
            }`}
          >
            <div className="flex items-center mb-1">
              <div
                className={`${item.color} w-5 h-5 border border-white mr-2`}
              ></div>
              <span className="text-white text-sm">{item.label}</span>
            </div>
            <span className="text-white text-xs bg-gray-700 px-2 py-1 rounded">
              {count} cases
            </span>
          </div>
        );
      })}
      {/* Add the deselect all button */}
      <div
        onClick={deselectAllCells}
        className="flex flex-col justify-center items-center cursor-pointer p-3 rounded bg-gray-500 hover:bg-gray-600"
      >
        <div className="flex items-center mb-1">
          <div className="w-5 h-5 border border-white mr-2 bg-gray-500"></div>
          <span className="text-white text-sm">Déselection</span>
        </div>
        <span className="text-white text-xs">Tout effacer</span>
      </div>
    </div>
  );
};

const CanvasGrid = ({
  width,
  height,
  maillage,
  selectedCells,
  handleCellClick,
  zvCellsInputData,
  removedRows = [],
  removedColumns = [],
}: {
  width: number;
  height: number;
  maillage: MaillageType;
  selectedCells: SelectedCellsType;
  handleCellClick: (row: number, col: number) => void;
  zvCellsInputData: boolean[][];
  removedRows?: number[]; // rows we want in pink
  removedColumns?: number[]; // columns we want in pink
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scaleWidth = 50; // Width of the color scale
  const squareHeight = height / 10; // Height of each square in the scale

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        const cellWidth = (width - scaleWidth) / 16; // Adjust the available width for cells
        const cellHeight = height / 12;

        // Clear the canvas before drawing
        context.clearRect(0, 0, width, height);

        // Draw the color scale squares
        for (let i = 0; i < 10; i++) {
          const grayValue = Math.floor((i / 10) * 255);
          const color = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
          context.fillStyle = color;
          context.fillRect(
            0,
            height - (i + 1) * squareHeight,
            scaleWidth,
            squareHeight
          );

          // Text for each 10% slice
          context.fillStyle = "white"; // Text contrast based on the background color
          context.font = "12px Arial";
          context.textAlign = "left";
          context.textBaseline = "middle";
          context.fillText(`${i * 10}%`, 5, height - (i + 0.5) * squareHeight);
        }

        // Draw the cells
        maillage.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            const successRate =
              cell.total > 0 ? (cell.successes / cell.total) * 100 : -1;
            const grayValue =
              successRate >= 0 ? Math.floor((successRate / 100) * 255) : 0;
            let color;
            const isRemovedRow = removedRows.includes(rowIndex);
            const isRemovedCol = removedColumns.includes(colIndex);

            if (isRemovedRow || isRemovedCol) {
              // Cells in removed row/col -> PINK
              color = "#006599";
            } else {
              // Otherwise, color based on cell value
              color =
                successRate >= 0
                  ? `rgb(${grayValue}, ${grayValue}, ${grayValue})`
                  : zvCellsInputData[rowIndex][colIndex]
                  ? "white" // Display in white if in ZV
                  : "black"; // Otherwise, display in black
            }

            context.fillStyle = color;
            context.fillRect(
              scaleWidth + colIndex * cellWidth,
              rowIndex * cellHeight,
              cellWidth,
              cellHeight
            );

            // if (selectedCells[rowIndex][colIndex] === 1) {
            //   context.strokeStyle = "yellow";
            //   context.lineWidth = 2;
            // } else if (selectedCells[rowIndex][colIndex] === 2) {
            //   context.strokeStyle = "red";
            //   context.lineWidth = 2;
            // } else if (selectedCells[rowIndex][colIndex] === 3) {
            //   context.strokeStyle = "green";
            //   context.lineWidth = 2;
            // } else {
            //   context.strokeStyle = "grey";
            //   context.lineWidth = 1;
            // }
            // context.strokeRect(
            //   scaleWidth + colIndex * cellWidth,
            //   rowIndex * cellHeight,
            //   cellWidth,
            //   cellHeight
            // ); // Grid lines
            // after you fill the background and draw your text…

            // 1) draw a consistent grey grid line around every cell
            context.strokeStyle = "grey";
            context.lineWidth = 1;
            context.strokeRect(
              scaleWidth + colIndex * cellWidth,
              rowIndex * cellHeight,
              cellWidth,
              cellHeight
            );

            // 2) draw the little circle indicator for *all* zones (including zone=0 as grey)
            const zone = selectedCells[rowIndex][colIndex];
            // choose a color for each zone
            const dotColor =
              zone === 1
                ? "yellow"
                : zone === 2
                ? "red"
                : zone === 3
                ? "green"
                : "grey"; // zone === 0

            // compute a non-negative radius
            const radius = Math.max(0, Math.min(cellWidth, cellHeight) * 0.1);
            const padding = radius * 0.5;
            const cx = scaleWidth + colIndex * cellWidth + padding + radius;
            const cy = rowIndex * cellHeight + padding + radius;

            context.beginPath();
            context.fillStyle = dotColor;
            context.arc(cx, cy, radius, 0, Math.PI * 2);
            context.fill();

            // Text for success rates (displayed only if total > 0)
            if (isRemovedRow || isRemovedCol) {
              context.fillStyle = "#006599";
            } else if (cell.total > 0) {
              const textColor = grayValue > 128 ? "black" : "white";
              context.fillStyle = textColor;
              context.textAlign = "center";
              context.textBaseline = "middle";
              const text = `${successRate.toFixed(0)}%`;
              context.fillText(
                text,
                scaleWidth + colIndex * cellWidth + cellWidth / 2,
                rowIndex * cellHeight + cellHeight / 2
              );
            }
          });
        });
        // Draw the central cross
        const centerX = (width + scaleWidth) / 2;
        const centerY = height / 2;
        const crossLength = Math.min(cellWidth, cellHeight) * 0.35;

        context.beginPath();
        context.strokeStyle = "blue"; // Adjust color as needed
        context.lineWidth = 4;
        context.moveTo(centerX - crossLength / 2, centerY);
        context.lineTo(centerX + crossLength / 2, centerY);
        context.stroke();

        context.beginPath();
        context.moveTo(centerX, centerY - crossLength / 2);
        context.lineTo(centerX, centerY + crossLength / 2);
        context.stroke();
      }
    }
  }, [
    maillage,
    selectedCells,
    width,
    height,
    scaleWidth,
    squareHeight,
    removedRows,
    removedColumns,
    zvCellsInputData,
  ]);

  const handleClick = (event: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const col = Math.floor((x - scaleWidth) / ((width - scaleWidth) / 16));
    const row = Math.floor(y / (height / 12));
    if (col >= 0 && col < 16) {
      handleCellClick(row, col);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={width + scaleWidth}
      height={height}
      onClick={handleClick}
      style={{ cursor: "pointer" }}
    />
  );
};

const ModifySelectionZF = forwardRef(
  (
    {
      selectionData,
      resultData,
      selectionRef,
    }: {
      selectionData: { selection: string };
      resultData: SessionResults;
      selectionRef: { current: { ZF: string[]; ZP: string[]; ZV: string[] } };
    },
    ref2
  ) => {
    const [ref, rect] = useResizeObserver();

    const [currentZone, setCurrentZone] = useState(0);
    // Initialize maillage with successes and total
    const [maillage, setMaillage] = useState(
      [...Array(12)].map(() =>
        Array(16)
          .fill(null)
          .map(() => ({ successes: 0, total: 0 }))
      )
    );
    const [selectedCells, setSelectedCells] = useState(
      [...Array(12)].map(() => Array(16).fill(0))
    );

    const [zvCellsInputData, setZvCellsInputData] = useState(
      [...Array(12)].map(() => Array(16).fill(false))
    );

    // Handle changes in selectionData and resultData props
    useEffect(() => {
      if (resultData) {
        // console.log("resultData", resultData);
        let jsonResults;

        try {
          if (resultData.resultsZF && resultData.resultsZF !== "null") {
            // console.log("resultData.resultsZF", resultData.resultsZF);
            jsonResults = JSON.parse(resultData.resultsZF);
          } else if (
            resultData.resultsZF === null ||
            resultData.resultsZF === "null"
          ) {
            // console.log("resultData.resultsZF is null");
            jsonResults = null;
          }

          // console.log("jsonResults", jsonResults);

          if (jsonResults) {
            const newResultData = jsonResults.map((result: ResultType) => {
              if (result.reactionTime) {
                return {
                  position: result.position,
                  successCount: result.reactionTime !== "Trop long" ? 1 : 0,
                  failedCount: result.reactionTime === "Trop long" ? 1 : 0,
                };
              } else {
                return {
                  position: result.position,
                  successCount: parseInt(result.successCount, 10),
                  failedCount: 0, // Assume failedCount is 0 if not provided
                };
              }
            });

            const newMaillage = [...Array(12)].map(() =>
              Array(16)
                .fill(null)
                .map(() => ({ successes: 0, total: 0 }))
            );

            newResultData.forEach((result: ResultType2) => {
              const { row, col } = convertPositionToIndices(result.position);

              // Increment values of successes and total
              newMaillage[row][col].successes += result.successCount;
              newMaillage[row][col].total +=
                result.successCount + result.failedCount;
            });
            setMaillage(newMaillage);
          } else {
            // Clear the maillage and selectedCells
            setMaillage(
              [...Array(12)].map(() =>
                Array(16)
                  .fill(null)
                  .map(() => ({ successes: 0, total: 0 }))
              )
            );
          }
        } catch (error) {
          console.error("Failed to parse JSON data:", error);
        }
      }
    }, [resultData]);

    useEffect(() => {
      if (resultData && resultData.visualFieldInput) {
        try {
          const visualFieldInput = JSON.parse(resultData.visualFieldInput);
          if (visualFieldInput && visualFieldInput.ZV) {
            const newZvCells = [...Array(12)].map(() => Array(16).fill(false));
            visualFieldInput.ZV.forEach((position: string) => {
              const { row, col } = convertPositionToIndices(position);
              newZvCells[row][col] = true;
            });
            setZvCellsInputData(newZvCells);
          } else {
            // Clear zvCells if no ZV data
            setZvCellsInputData(
              [...Array(12)].map(() => Array(16).fill(false))
            );
          }
        } catch (error) {
          console.error("Failed to parse visualFieldInput:", error);
        }
      }
    }, [resultData]);

    const convertPositionToIndices = (position: string) => {
      const letter = position.charAt(0);
      const number = parseInt(position.slice(1), 10);
      const col = letter.charCodeAt(0) - 65; // Convert letter to column index (A=0, B=1, ..., O=14, P=15)
      const row = number - 1; // Convert number to row index (1=0, 2=1, ..., 12=11)
      return { row, col };
    };

    useEffect(() => {
      if (selectionData) {
        let jsonResults;

        try {
          if (selectionData.selection && selectionData.selection !== "null") {
            jsonResults = JSON.parse(selectionData.selection);
          } else if (
            selectionData.selection === null ||
            selectionData.selection === "null"
          ) {
            jsonResults = null;
          }
          if (jsonResults) {
            if (jsonResults.ZP || jsonResults.ZF || jsonResults.ZV) {
              // It's a selection file
              const newSelectedCells = [...Array(12)].map(() =>
                Array(16).fill(0)
              );
              jsonResults.ZP.forEach((position: string) => {
                const { row, col } = convertPositionToIndices(position);
                newSelectedCells[row][col] = 2; // Red
              });
              jsonResults.ZF.forEach((position: string) => {
                const { row, col } = convertPositionToIndices(position);
                newSelectedCells[row][col] = 1; // Yellow
              });
              jsonResults.ZV.forEach((position: string) => {
                const { row, col } = convertPositionToIndices(position);
                newSelectedCells[row][col] = 3; // Green
              });
              setSelectedCells(newSelectedCells);
            }
          } else {
            // Clear the selectedCells
            setSelectedCells([...Array(12)].map(() => Array(16).fill(0)));
          }
        } catch (error) {
          console.error("Failed to parse JSON data:", error);
        }
      }
    }, [selectionData]);

    const handleCellClick = (row: number, col: number) => {
      const newSelectedCells = [...selectedCells];
      // newSelectedCells[row][col] = (newSelectedCells[row][col] + 1) % 4; // Cycle through 0, 1, 2, 3
      // console.log("newSelectedCells", newSelectedCells)
      newSelectedCells[row][col] = currentZone; // Apply the current selected zone
      setSelectedCells(newSelectedCells);
    };

    // const selectAllLowerThan100AndNearby = () => {
    //   const newSelectedCells = [...Array(12)].map(() => Array(16).fill(0));

    //   const directions = [
    //     [-1, -1], [-1, 0], [-1, 1],
    //     [0, -1], [0, 1],
    //     [1, -1], [1, 0], [1, 1]
    //   ];

    //   maillage.forEach((row, rowIndex) => {
    //     row.forEach((cell, colIndex) => {
    //       const successRate = cell.total > 0 ? (cell.successes / cell.total) * 100 : -1;
    //       if (successRate >= 0 && successRate < 100) {
    //         newSelectedCells[rowIndex][colIndex] = 1; // Yellow
    //         directions.forEach(([dx, dy]) => {
    //           const newRow = rowIndex + dx;
    //           const newCol = colIndex + dy;
    //           if (newRow >= 0 && newRow < 12 && newCol >= 0 && newCol < 16) {
    //             newSelectedCells[newRow][newCol] = 1; // Yellow
    //           }
    //         });
    //       }
    //     });
    //   });
    //   setSelectedCells(newSelectedCells);
    // };

    const deselectAllCells = () => {
      const newSelectedCells = [...Array(12)].map(() => Array(16).fill(0)); // Set all cells to 0 (neutral)
      setSelectedCells(newSelectedCells);
    };

    const autoSelectCells = () => {
      const newSelection = evolveState(
        JSON.parse(resultData.resultsZF),
        JSON.parse(resultData.visualFieldInput)
      );
      if (newSelection) {
        if (newSelection.ZP || newSelection.ZF || newSelection.ZV) {
          // It's a selection file
          const newSelectedCells = [...Array(12)].map(() => Array(16).fill(0));
          newSelection.ZP.forEach((position: string) => {
            const { row, col } = convertPositionToIndices(position);
            newSelectedCells[row][col] = 2; // Red
          });
          newSelection.ZF.forEach((position: string) => {
            const { row, col } = convertPositionToIndices(position);
            newSelectedCells[row][col] = 1; // Yellow
          });
          newSelection.ZV.forEach((position: string) => {
            const { row, col } = convertPositionToIndices(position);
            newSelectedCells[row][col] = 3; // Green
          });
          setSelectedCells(newSelectedCells);
        }
      } else {
        // Clear the selectedCells
        setSelectedCells([...Array(12)].map(() => Array(16).fill(0)));
      }
      // setSelectedCells(newSelection);
    };

    useImperativeHandle(ref2, () => ({
      triggerAutoSelectCells: () => {
        autoSelectCells();
      },
      triggerDownloadModifiedData: () => {
        downloadNewResults();
      },
    }));

    const downloadNewResults = () => {
      const convertToGridNotation = (rowIndex: number, colIndex: number) => {
        const column = String.fromCharCode(65 + colIndex);
        const row = rowIndex + 1;
        return `${column}${row}`;
      };

      const newResults: {
        ZF: string[];
        ZP: string[];
        ZV: string[];
      } = {
        ZF: [],
        ZP: [],
        ZV: [],
      };

      selectedCells.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell === 1) {
            newResults.ZF.push(convertToGridNotation(rowIndex, colIndex));
          } else if (cell === 2) {
            newResults.ZP.push(convertToGridNotation(rowIndex, colIndex));
          } else if (cell === 3) {
            newResults.ZV.push(convertToGridNotation(rowIndex, colIndex));
          }
        });
      });
      selectionRef.current = newResults;
      // console.log("newResults", newResults);
    };

    // // Expose methods via selectionRef
    // useEffect(() => {
    //   if (selectionRef) {
    //     selectionRef.current = {
    //       selectAllLowerThan100AndNearby,
    //       downloadNewResults
    //     };
    //   }
    // }, [selectionRef]);
    const maxWidth = rect.width > 1400 ? 1400 : rect.width;

    return (
      <div ref={ref}>
        <CanvasGrid
          width={maxWidth}
          height={maxWidth * 0.75}
          maillage={maillage}
          selectedCells={selectedCells}
          handleCellClick={handleCellClick}
          zvCellsInputData={zvCellsInputData}
          removedRows={resultData.removedRows}
          removedColumns={resultData.removedColumns}
        />
        <Legend
          currentZone={currentZone}
          setCurrentZone={setCurrentZone}
          deselectAllCells={deselectAllCells}
          selectedCells={selectedCells}
        />{" "}
      </div>
    );
  }
);

export default ModifySelectionZF;
